/**
 * Browser print for PrintService receipt HTML (80mm).
 * After async payment, Chrome blocks print unless a window was opened
 * on the Pay click (user gesture). prepareBillPrintWindow() does that.
 */

let preparedPrintWin = null;
let lastPrintKey = "";
let lastPrintAt = 0;

/** Strip auto-print hooks from server HTML — we invoke print() ourselves once. */
function receiptHtml(printJob, documentTitle) {
  let html = String(printJob.html || "");
  html = html.replace(/\s+onload\s*=\s*(['"])[\s\S]*?\1/gi, "");
  html = html.replace(/<script\b[^>]*>[\s\S]*?window\.print\s*\([\s\S]*?<\/script>/gi, "");
  if (documentTitle) {
    html = html.replace(/<title>[^<]*<\/title>/i, `<title>${documentTitle}</title>`);
  }
  return html;
}

function runPrint(win) {
  try {
    win.focus();
    win.print();
    return true;
  } catch {
    return false;
  }
}

/** Call synchronously from the Pay / Print click, before the API request. */
export function prepareBillPrintWindow() {
  cancelPreparedBillPrint();
  try {
    const w = window.open("", "pos-receipt-print", "width=420,height=720");
    if (!w) return false;
    preparedPrintWin = w;
    w.document.open();
    w.document.write(
      "<!DOCTYPE html><html><head><title>Receipt</title></head><body style=\"font-family:sans-serif;padding:16px;color:#444\">Preparing receipt…</body></html>",
    );
    w.document.close();
    return true;
  } catch {
    preparedPrintWin = null;
    return false;
  }
}

export function cancelPreparedBillPrint() {
  if (preparedPrintWin && !preparedPrintWin.closed) {
    try {
      preparedPrintWin.close();
    } catch {
      /* ignore */
    }
  }
  preparedPrintWin = null;
}

export function printBillReceipt(printJob, documentTitle) {
  if (!printJob?.html) return false;

  const title = documentTitle || printJob.bill_number || "Receipt";
  const key = String(printJob.bill_id || printJob.bill_number || title);
  const now = Date.now();
  // Guard against double onSuccess / Enter double-fire within the same payment.
  if (key && key === lastPrintKey && now - lastPrintAt < 4000) {
    return true;
  }
  lastPrintKey = key;
  lastPrintAt = now;

  const html = receiptHtml(printJob, title);
  const win = preparedPrintWin && !preparedPrintWin.closed ? preparedPrintWin : null;
  preparedPrintWin = null;

  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.document.title = title;
    // Single trigger only — do not also bind win.onload (HTML body onload is stripped).
    setTimeout(() => {
      if (!win.closed) runPrint(win);
    }, 300);
    return true;
  }

  return printViaIframe(html, printJob.paper_width_mm || 80, title);
}

function printViaIframe(html, paperMm, title) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = [
    "position:fixed",
    "left:-9999px",
    "top:0",
    `width:${paperMm}mm`,
    "height:800px",
    "border:0",
  ].join(";");
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!doc) {
    iframe.remove();
    return false;
  }

  let didPrint = false;
  const triggerPrint = () => {
    if (didPrint) return;
    didPrint = true;
    runPrint(win);
    setTimeout(() => iframe.remove(), 60_000);
  };

  doc.open();
  doc.write(html);
  doc.title = title;
  doc.close();
  setTimeout(triggerPrint, 300);

  return true;
}
